// @flow

import type { TranslateFn } from '../../controls/locale';
import type { PlaceData } from '../AggregatesListings/useAggregatesListingsPlaceData';
import { translatePropertyMainType } from './propertyTypes';

export const getNearLocation = (t: TranslateFn, data: PlaceData): string => {
  switch (data.placeType) {
    case 'other':
      return '';
    case 'country':
      return t('nearCountry', { country: data.label });
    case 'state':
      return t('nearState', { state: data.label });
    case 'district':
      return t('nearDistrict', { district: data.label });
    case 'neighbourhood':
      return t('nearNeighbourhood', { neighbourhood: data.label });
    default:
      return t('nearMunicipality', { municipality: data.label });
  }
};

export const getPlaceTitle = (
  t: TranslateFn,
  data: PlaceData,
  type: 'within' | 'near' | 'by_rooms' | 'plural',
): string => {
  const vars = {
    inLocation: data.inLabel,
    nearLocation: getNearLocation(t, data),
    ...translatePropertyMainType(t, data.propertyType ?? ''),
  };

  if (type === 'by_rooms') {
    return data.offerType === 'buy'
      ? t('propertiesForSaleByRooms', { ...vars, count: null })
      : t('propertiesForRentByRooms', { ...vars, count: null });
  }

  if (data.rooms != null) {
    const { lte, gte } = data.rooms;
    const vars2 = {
      ...vars,
      rooms: lte == null ? `${gte}+` : `${gte} – ${lte}`,
    };
    if (type === 'near') {
      return data.offerType === 'buy'
        ? t('xRoomPropertiesForSaleNearLocation', { ...vars2, count: null })
        : t('xRoomPropertiesForRentNearLocation', { ...vars2, count: null });
    }
    return data.offerType === 'buy'
      ? t('xRoomPropertiesForSaleInLocation', { ...vars2, count: null })
      : t('xRoomPropertiesForRentInLocation', { ...vars2, count: null });
  }

  if (type === 'near') {
    return data.offerType === 'buy'
      ? t('propertiesForSaleNearLocation', { ...vars, count: null })
      : t('propertiesForRentNearLocation', { ...vars, count: null });
  }

  if (type === 'plural') {
    return data.offerType === 'buy'
      ? t('propertiesForSaleInLocationPlural', { ...vars, count: null })
      : t('propertiesForRentInLocationPlural', { ...vars, count: null });
  }

  return data.offerType === 'buy'
    ? t('propertiesForSaleInLocation', { ...vars, count: null })
    : t('propertiesForRentInLocation', { ...vars, count: null });
};

export const getPlaceSubTitle = (t: TranslateFn, data: PlaceData): string => {
  const vars = {
    inLocation: data.inLabel,
    ...translatePropertyMainType(t, data.propertyType ?? ''),
  };

  if (data.rooms != null) {
    const count = data.rooms.gte;
    return data.offerType === 'buy'
      ? t('buyPropertyNRoomsInLocation', { ...vars, count })
      : t('rentPropertyNRoomsInLocation', { ...vars, count });
  }

  return data.offerType === 'buy'
    ? t('buyPropertyInLocation', { ...vars, count: null })
    : t('rentPropertyInLocation', { ...vars, count: null });
};
